import React, { useMemo } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";

export type DailyPageviewsPoint = { date: string; pageviews: number };

/**
 * Convert ISO date (yyyy-mm-dd) to dd.mm for the X-axis label.
 */
function formatDateLabel(iso: string) {
  const s = String(iso ?? "");
  const parts = s.split("-");
  if (parts.length !== 3) return s;
  const mm = parts[1];
  const dd = parts[2];
  return `${dd}.${mm}`;
}

export function DailyPageviewsChart({
  data,
  t,
  lineColor,
}: {
  data: DailyPageviewsPoint[];
  t: (key: string) => string;
  lineColor?: string;
}) {
  const safe = Array.isArray(data) ? data : [];
  const resolvedLineColor = lineColor ?? "var(--category-scale-3)";
  const secondaryTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 66%, var(--surface))",
  } as const;

  const mapped = useMemo(
    () =>
      safe.map((d) => ({
        date: String(d.date ?? ""),
        pageviews: Number(d.pageviews ?? 0),
        dayLabel: formatDateLabel(d.date),
      })),
    [safe]
  );

  if (mapped.length === 0) {
    return (
      <div className="text-sm" style={secondaryTextStyle}>
        {t("statistics.noData")}
      </div>
    );
  }

  const from = mapped[0]?.date ?? "";
  const to = mapped[mapped.length - 1]?.date ?? "";

  // If all values are 0, force a non-zero range so a flat line is still rendered.
  const maxY = mapped.reduce((m, p) => Math.max(m, p.pageviews), 0);
  const yDomain: [number, number] = maxY === 0 ? [0, 1] : [0, maxY];

  // Avoid clutter: show about ~10 ticks across 30 days.
  const xInterval = Math.max(0, Math.floor(mapped.length / 10));

  return (
    <div className="w-full">
      <div className="mb-2 text-xs" style={secondaryTextStyle}>
        {from && to
          ? (t("statistics.rangeLabel") || "Range: {{from}} → {{to}}")
              .replace("{{from}}", from)
              .replace("{{to}}", to)
          : ""}
      </div>

      {/* IMPORTANT: Use inline height so it cannot be broken by Tailwind build/safelist */}
      <div
        style={{ width: "100%", height: 280 }}
        className="rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"
      >
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={mapped} margin={{ top: 16, right: 20, left: 24, bottom: 20 }}>
            <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />

            <XAxis
              dataKey="dayLabel"
              interval={xInterval}
              minTickGap={12}
              tickMargin={10}
            />

            <YAxis
              domain={yDomain}
              allowDecimals={false}
              tickMargin={10}
            />

            <Tooltip
              formatter={(value) => [
                Number(value),
                t("statistics.pageviewsLabel"),
              ]}
              labelFormatter={(_, payload) => {
                const original = payload?.[0]?.payload?.date as string | undefined;
                const label = t("statistics.dateLabel");
                return original ? `${label}: ${original}` : label;
              }}
              cursor={{ stroke: "var(--border)" }}
              contentStyle={{
                background: "var(--card)",
                border: "1px solid var(--border)",
                color: "var(--foreground)",
                borderRadius: 8,
              }}
            />

            <Line
              type="monotone"
              dataKey="pageviews"
              stroke={resolvedLineColor}
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
